import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Loader2, Building2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function BillContacts() {
  const [company, setCompany] = useState({ name: '', address: '', email: '' });
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingCompany, setSavingCompany] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', address: '', email: '' });
  const [savingContact, setSavingContact] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [companyRes, contactsRes] = await Promise.all([
        axios.get(`${API_URL}/bill-company`, { headers: authHeaders() }),
        axios.get(`${API_URL}/bill-contacts`, { headers: authHeaders() }),
      ]);
      setCompany(companyRes.data);
      setContacts(contactsRes.data || []);
    } catch (e) {
      console.error('Failed to fetch:', e);
    } finally {
      setLoading(false);
    }
  };

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await axios.put(`${API_URL}/bill-company`, {
        name: company.name, address: company.address, email: company.email,
      }, { headers: authHeaders() });
      toast.success('Company info saved');
    } catch (e) {
      toast.error('Failed to save company info');
    } finally {
      setSavingCompany(false);
    }
  };

  const openDialog = (contact = null) => {
    setEditContact(contact);
    setContactForm(contact ? { name: contact.name, address: contact.address || '', email: contact.email || '' } : { name: '', address: '', email: '' });
    setShowDialog(true);
  };

  const saveContact = async () => {
    if (!contactForm.name.trim()) return toast.error('Name is required');
    setSavingContact(true);
    try {
      if (editContact) {
        await axios.put(`${API_URL}/bill-contacts/${editContact.contact_id}`, contactForm, { headers: authHeaders() });
        toast.success('Contact updated');
      } else {
        await axios.post(`${API_URL}/bill-contacts`, contactForm, { headers: authHeaders() });
        toast.success('Contact added');
      }
      setShowDialog(false);
      fetchAll();
    } catch (e) {
      toast.error('Failed to save contact');
    } finally {
      setSavingContact(false);
    }
  };

  const deleteContact = async (contact) => {
    if (!window.confirm(`Delete contact "${contact.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/bill-contacts/${contact.contact_id}`, { headers: authHeaders() });
      toast.success('Contact deleted');
      fetchAll();
    } catch (e) {
      toast.error('Failed to delete contact');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-zinc-500" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* My Company (sender) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-white">
            <Building2 className="w-5 h-5" /> My Company (sender on bills)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Company Name</Label>
              <Input value={company.name || ''} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="company-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={company.email || ''} onChange={(e) => setCompany({ ...company, email: e.target.value })} data-testid="company-email-input" />
            </div>
          </div>
          <div>
            <Label>Address (one line per row)</Label>
            <Textarea rows={3} value={company.address || ''} onChange={(e) => setCompany({ ...company, address: e.target.value })} data-testid="company-address-input" />
          </div>
          <Button onClick={saveCompany} disabled={savingCompany} data-testid="company-save-btn">
            {savingCompany ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </CardContent>
      </Card>

      {/* Client contacts */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg text-white">Client Contacts</CardTitle>
          <Button size="sm" onClick={() => openDialog()} data-testid="add-contact-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Contact
          </Button>
        </CardHeader>
        <CardContent>
          {contacts.length === 0 ? (
            <p className="text-zinc-500 text-sm py-6 text-center" data-testid="contacts-empty">No contacts yet. Add your first client.</p>
          ) : (
            <div className="space-y-2">
              {contacts.map((c) => (
                <div key={c.contact_id} className="flex items-start justify-between p-3 rounded-md border border-zinc-800 hover:bg-zinc-900/50" data-testid={`contact-row-${c.contact_id}`}>
                  <div>
                    <p className="text-white font-medium">{c.name}</p>
                    {c.email && <p className="text-zinc-400 text-sm">{c.email}</p>}
                    {c.address && <p className="text-zinc-500 text-sm whitespace-pre-line">{c.address}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(c)} data-testid={`contact-edit-${c.contact_id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteContact(c)} data-testid={`contact-delete-${c.contact_id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">{editContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
            <DialogDescription>Contact details shown on your bills.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Fanplace LLC" data-testid="contact-name-input" />
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} data-testid="contact-email-input" />
            </div>
            <div>
              <Label>Address (one line per row)</Label>
              <Textarea rows={3} value={contactForm.address} onChange={(e) => setContactForm({ ...contactForm, address: e.target.value })} placeholder={'304 S Jones Blvd. #4037\nLas Vegas, NV, 89107\nUS'} data-testid="contact-address-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveContact} disabled={savingContact} data-testid="contact-save-btn">
              {savingContact && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
